import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import {
  getBaseUrl,
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
  buildFAQPageSchema,
} from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const TITLE = "Atendimento automático no WhatsApp Business com IA | FabChat";
const DESCRIPTION =
  "Atendimento automático no WhatsApp Business com agente de IA: respostas com contexto, qualificação de leads e CRM integrado em um único painel.";

const BREADCRUMB = [
  { name: "Início", path: "/" },
  { name: "Atendimento automático no WhatsApp", path: "/whatsapp/atendimento-automatico" },
];

const FAQ_ITEMS = [
  {
    question: "Como funciona o atendimento automático no WhatsApp?",
    answer:
      "Na FabChat, o atendimento automático é feito por um agente de IA conectado ao WhatsApp Business. Ele responde com contexto, segue regras configuradas pela empresa, qualifica leads e encaminha para um humano quando necessário — sem fluxos engessados. É o novo método para criar relacionamento com o cliente em escala.",
  },
  {
    question: "Posso configurar horários de atendimento?",
    answer:
      "Sim. Você define quando o agente atende sozinho, quando avisa que está fora do expediente e quando encaminha para um humano. Tudo no painel, sem código.",
  },
  {
    question: "Mensagem automática no WhatsApp é o mesmo que agente de IA?",
    answer:
      "Não. Mensagem automática costuma ser uma resposta única e fixa. O agente de IA da FabChat entende a intenção do cliente, mantém o contexto da conversa e adapta a resposta dentro das regras configuradas, criando uma experiência próxima a um atendente real.",
  },
  {
    question: "O atendimento automático substitui a equipe humana?",
    answer:
      "Não substitui. O agente cuida do primeiro contato, das dúvidas frequentes e da qualificação; o time humano assume nos momentos certos, com todo o histórico no painel. É a combinação que escala o relacionamento sem perder qualidade.",
  },
];

export default function WhatsAppAtendimentoAutomatico() {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, BREADCRUMB);
  const organizationSchema = buildOrganizationSchema(baseUrl);
  const softwareSchema = buildSoftwareApplicationSchema(baseUrl, DESCRIPTION);
  const faqSchema = buildFAQPageSchema(FAQ_ITEMS);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead title={TITLE} description={DESCRIPTION} canonicalPath="/whatsapp/atendimento-automatico" />
      <StructuredData data={[organizationSchema, softwareSchema, breadcrumbSchema, faqSchema]} />
      <Header />

      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 md:py-24 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Atendimento automático no WhatsApp
          </h1>
          <p className="text-lg text-muted-foreground mb-12">
            Quem busca <strong className="text-foreground">atendimento automático no WhatsApp</strong> geralmente quer responder contatos em escala sem perder qualidade. Na FabChat, isso é feito com <strong className="text-foreground">agentes de IA</strong>: você configura o comportamento, os horários e as regras; a IA mantém o contexto e direciona para vendas, suporte ou agendamento.
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">O que o atendimento automático entrega</h2>
            <p className="text-muted-foreground mb-4">
              Respostas automáticas no WhatsApp Business, qualificação de leads, follow-up e, quando quiser, integração com calendário para agendamentos. Tudo isso a FabChat oferece por meio do <strong className="text-foreground">agente de IA</strong> conectado ao seu número — não como “módulo de atendimento automático” à parte, e sim como um assistente conversacional configurável. O histórico fica no mesmo painel em que a equipe atende e no <Link to="/integracoes/crm" className="text-primary hover:underline">CRM integrado</Link>.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Conheça o agente de IA para WhatsApp</h2>
            <p className="text-muted-foreground mb-4">
              A página do produto explica em detalhe: personalidade, regras, horários e integração com a operação. Também temos página para quem busca <Link to="/whatsapp/chatbot" className="text-primary hover:underline">chatbot para WhatsApp</Link> e <Link to="/instagram/agente-ia" className="text-primary hover:underline">agente para Instagram</Link>.
            </p>
          </section>

          <section className="flex flex-col sm:flex-row gap-4 pt-6">
            <Button asChild size="lg" className="gap-2">
              <Link to="/whatsapp/agente-ia">
                Ver agente de IA para WhatsApp <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/planos">Ver planos</Link>
            </Button>
            <Button asChild variant="ghost" size="lg">
              <Link to="/contato">Falar com a gente</Link>
            </Button>
          </section>
        </section>
      </main>

      <Footer />
    </div>
  );
}
